import { storeToRefs } from 'pinia';
import { useSelectorStepsStore } from '~/stores/useSelectorStepsStore';
import useProducts from '~/composable/useProducts';
import useCategories from '~/composable/useCategories';
import useTlmCart from '~/composable/useTlmCart';
import usePlaymix from '~/composable/usePlaymix';
import { MODEM_TYPE } from '~/constants/ModemType';
import { subscriptionHasPlayServices } from '~/composable/useSubscriptionPlayServices';

export default function useSelectorSteps(appContext) {
  const app = appContext?.app ?? appContext
  const hubbleClient = app?.$hubble
  const selectorStepsStore = useSelectorStepsStore()
  const {
    model,
    selectedRouterSku,
    playServicesSelected,
    routers,
    subscription,
    category,
    hasError
  } = storeToRefs(selectorStepsStore)
  const { getProductsGroupedBySubscription } = useProducts(hubbleClient)
  const { getCategory } = useCategories(hubbleClient)
  const { addCartItem, loadOrCreateCart, createCart } = useTlmCart(appContext);
  const { selectedPlayServices, data: offeredMixServices } = usePlaymix('playmix-4-for-internet', hubbleClient)
  const getProductsBySubscription = async (categorySlug, subscriptionId) => {
    category.value = await getCategory({ categorySlug })
    const res = await getProductsGroupedBySubscription(category?.value?.id, subscriptionId).catch(() => {
      hasError.value = true
    })
    routers.value = res?.products
    subscription.value = res?.subscriptionBundleDTO
  }
  const getSubscriptionPrice = (prices, subscriptionId) => {
    return prices.find(price => price.channelKey === subscriptionId)
  }
  const setCategory = async(categorySlug) => {
    category.value = await getCategory({ categorySlug })
  }
  const addToBasketBtnClicked = async ({
    channelSplit = null,
    subscriptionId,
    selectedMixServices = null,
    selectedPartnerAddons = null
  }) => {
    if (channelSplit !== null) {
      channelSplit = 'channelsplit-' + channelSplit
    }
    if (selectedMixServices != null) {
      selectedPlayServices.value = selectedMixServices
    }
    const res = await addCartItem({
      sku: selectedRouterSku.value,
      channelKey: subscriptionId,
      mixProducts: subscriptionHasPlayServices(subscription?.value) ? selectedPlayServices.value : null,
      partnerAddons: selectedPartnerAddons,
      categoryId: category?.value?.id,
      channelSplit
    })
    return { ...res, categoryId: category?.value?.id }
  }
  const goBack = () => {
    model.value = 1;
  }
  const setStep = (nr) => {
    model.value = nr
    window?.scrollTo({ top: 50, behavior: 'smooth' })
  }
  const loadCart = async () => {
    await loadOrCreateCart()
  }
  const getRouterListForSubscription = async (subscriptionId) => {
    await getProductsBySubscription('modems', subscriptionId)
  }
  const getProductsForSubscription = async (subscriptionId) => {
    await getProductsBySubscription('subscriptions', subscriptionId)
  }
  const getSteps = (categorySlug, selectedMixServices) => {
    if (categorySlug === 'modems') {
      return getStepsForInternet(selectedMixServices)
    } else if (categorySlug === 'subscriptions') {
      return getStepsForSubscription(selectedMixServices)
    } else {
      return null
    }
  }
  const getStepsForInternet = (selectedMixServices) => {
    if (subscriptionHasPlayServices(subscription?.value) && !hasValidMixServices(selectedMixServices)) {
      return [
        'internet-play-mix-selector',
        'internet-router-list'
      ]
    } else if (subscription?.value) {
      selectedPlayServices.value = selectedMixServices
      return [
        'internet-router-list'
      ]
    } else {
      return null
    }
  }
  const checkMixServiceIds = (selectedMixServices) => {
    const listOfPoIDS = []
    offeredMixServices?.value?.productOfferingDTOWithContent.forEach(service => {
      listOfPoIDS.push(service.productOfferingDTO.id)
    })
    selectedMixServices?.forEach(mixService => {
      if (!listOfPoIDS.includes(mixService)) {
        return false
      }
    })
    return true
  }
  const hasValidMixServices = (selectedMixServices) => {
    return selectedMixServices !== undefined && checkMixServiceIds(selectedMixServices)
  }
  const getStepsForSubscription = (selectedMixServices, isMaxVasLimit) => {
    if (subscriptionHasPlayServices(subscription?.value) && !hasValidMixServices(selectedMixServices)) {
      return isMaxVasLimit
        ? [
            'play-mix-tile-selector'
          ]
        : [
            'play-mix-tile-selector',
            'play-mix-accordion-selector'
          ]
    } else if (subscription?.value) {
      selectedPlayServices.value = selectedMixServices
      return []
    } else {
      return null
    }
  }
  const isLoanModem = (router) => {
    const routerAttrs = router?.productAttributes?.find(attr => attr.name === 'ModemType')
    const modemType = routerAttrs !== undefined
      ? routerAttrs.value
      : null
    return modemType === MODEM_TYPE.LOAN || modemType === MODEM_TYPE.REFURBISHED
  }
  return {
    setCategory,
    getStepsForInternet,
    hasError,
    setStep,
    getStepsForSubscription,
    getProductsForSubscription,
    isLoanModem,
    getSteps,
    getRouterListForSubscription,
    loadCart,
    goBack,
    model,
    selectedRouterSku,
    addToBasketBtnClicked,
    playServicesSelected,
    getSubscriptionPrice,
    getProductsBySubscription,
    routers,
    subscription,
    createCart
  }
}
